import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

type ColorScheme = 'light' | 'dark';

const STORAGE_KEY = 'golugati-color-scheme';

const ColorSchemeContext = createContext<{
  scheme: ColorScheme;
  toggle: () => void;
} | null>(null);

function getInitialScheme(): ColorScheme {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ColorSchemeProvider({ children }: { children: ReactNode }) {
  const [scheme, setScheme] = useState<ColorScheme>(getInitialScheme);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', scheme === 'dark');
    window.localStorage.setItem(STORAGE_KEY, scheme);
  }, [scheme]);

  function toggle() {
    setScheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }

  return <ColorSchemeContext.Provider value={{ scheme, toggle }}>{children}</ColorSchemeContext.Provider>;
}

export function useColorScheme() {
  const context = useContext(ColorSchemeContext);
  if (!context) {
    throw new Error('useColorScheme must be used within a ColorSchemeProvider');
  }
  return context;
}
